import { useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import TsdScanCapture from '../tsd/TsdScanCapture';
import {
  readPalletPrintAfterClose,
  writePalletPrintAfterClose,
} from '../../pages/fulfillmentUi.mjs';

function PalletLabelPrintButton({ busy, canPrint, onClick }) {
  const button = (
    <Button
      fullWidth
      variant="outlined"
      color="primary"
      onClick={onClick}
      disabled={busy || !canPrint}
      sx={{ minHeight: 56 }}
    >
      {busy ? <CircularProgress size={22} /> : 'Печать этикетки'}
    </Button>
  );
  if (canPrint) return button;
  return (
    <Tooltip title="Выберите принтер в настройках печати">
      <span style={{ display: 'block', width: '100%' }}>{button}</span>
    </Tooltip>
  );
}

/**
 * Закрытие палеты.
 *
 * TSD (requireScan=true, default):
 * 1) confirm — «ШК палеты наклеен?» + перепечатка
 * 2) verify — сверка сканом (PLT… или ПЛ-…)
 * 3) onCloseConfirm(scannedCode) → POST close
 *
 * ARM supervisor (requireScan=false):
 * одношаговое подтверждение без скана → onCloseConfirm() / onCloseConfirm(null)
 *
 * externalScanMode — HID-скан идёт в скрытое поле страницы; здесь только кнопка ручного ввода.
 */
export default function PalletCloseDialog({
  open,
  pallet,
  step = 'confirm', // confirm | verify
  onStepChange,
  busy = false,
  error = '',
  onClearError,
  externalScanMode = false,
  scannedCode = '',
  onScannedCodeChange,
  onReprint,
  onCloseConfirm,
  onCancel,
  onCancelPallet = null,
  onRemoveDeparted = null,
  requireScan = true,
  canPrint = false,
}) {
  const [printAfterClose, setPrintAfterClose] = useState(readPalletPrintAfterClose);
  const code = String(scannedCode || '').trim();
  const codeLabel = pallet?.code || (pallet?.id ? `#${pallet.id}` : '');
  const showRemoveDeparted = Boolean(onRemoveDeparted)
    && /выбыл|отмен|orders_left_mp|уже выбыл/i.test(String(error || ''));
  const scanMode = requireScan !== false;
  // Пустую палету закрыть нельзя ни супервайзеру, ни с ТСД — её отменяют.
  const isEmpty = Number(pallet?.items_count || 0) < 1;
  const offerCancel = isEmpty && Boolean(onCancelPallet);

  const handleConfirmApplied = () => {
    onScannedCodeChange?.('');
    onClearError?.();
    onStepChange?.('verify');
  };

  const handleSubmitVerify = (event) => {
    event?.preventDefault?.();
    if (!code || busy) return;
    onCloseConfirm?.(code);
  };

  const handleSupervisorClose = () => {
    if (busy) return;
    onCloseConfirm?.(null);
  };

  return (
    <Dialog
      open={open && Boolean(pallet)}
      onClose={() => { if (!busy) onCancel?.(); }}
      disableEscapeKeyDown={busy}
      disableAutoFocus
      disableEnforceFocus={externalScanMode}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle sx={{ pb: 1 }}>
        Закрыть палету {codeLabel}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          {!scanMode && (
            <Typography color="text.secondary" align="center">
              {isEmpty
                ? 'Палета пустая — закрыть её нельзя. Если создали по ошибке, отмените палету.'
                : 'Закрытие без сверки ШК (АРМ начальника). Отмены на МП по-прежнему блокируют закрытие.'}
            </Typography>
          )}
          {scanMode && step === 'confirm' && (
            <Typography color="text.secondary" align="center">
              Наклей этикетку палеты. Если не вышла — распечатай повторно.
            </Typography>
          )}
          {scanMode && step === 'verify' && (
            <TsdScanCapture
              capture={!externalScanMode}
              disabled={busy}
              onScan={(value) => {
                onScannedCodeChange?.(value);
                onClearError?.();
              }}
              helperText={code
                ? 'ШК считан — нажми «Закрыть палету»'
                : 'Сканируй ШК палеты — затем «Закрыть палету»'}
              label="Ввести ШК вручную"
              dialogTitle="Ручной ввод ШК палеты"
              dialogPlaceholder="ШК палеты"
            />
          )}
          <FormControlLabel
            control={(
              <Checkbox
                checked={printAfterClose}
                onChange={(event) => {
                  const next = event.target.checked;
                  setPrintAfterClose(next);
                  writePalletPrintAfterClose(next);
                }}
              />
            )}
            label="Печатать этикетку после закрытия"
          />
          {error && (
            <Alert severity="error" onClose={() => onClearError?.()}>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{error}</Typography>
              {showRemoveDeparted && (
                <Button
                  color="inherit"
                  size="small"
                  sx={{ mt: 1 }}
                  disabled={busy}
                  onClick={() => onRemoveDeparted?.(pallet)}
                >
                  Убрать выбывшие
                </Button>
              )}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2, gap: 1, flexDirection: 'column' }}>
        {!scanMode && (
          <>
            {offerCancel ? (
              <Button
                fullWidth
                variant="contained"
                color="error"
                onClick={() => onCancelPallet?.(pallet)}
                disabled={busy}
                sx={{ minHeight: 56 }}
              >
                Отменить палету
              </Button>
            ) : (
              <Button
                fullWidth
                variant="contained"
                color="warning"
                onClick={handleSupervisorClose}
                disabled={busy}
                sx={{ minHeight: 56 }}
              >
                {busy ? <CircularProgress size={22} /> : 'Закрыть палету'}
              </Button>
            )}
            {onReprint && (
              <PalletLabelPrintButton busy={busy} canPrint={canPrint} onClick={onReprint} />
            )}
          </>
        )}
        {scanMode && step === 'confirm' && (
          <>
            <Button
              fullWidth
              variant="contained"
              onClick={handleConfirmApplied}
              disabled={busy}
              sx={{ minHeight: 56 }}
            >
              ШК палеты наклеен
            </Button>
            <PalletLabelPrintButton busy={busy} canPrint={canPrint} onClick={onReprint} />
          </>
        )}
        {scanMode && step === 'verify' && (
          <>
            <Button
              fullWidth
              variant="contained"
              color="warning"
              onClick={handleSubmitVerify}
              disabled={busy || !code}
              sx={{ minHeight: 56 }}
            >
              {busy ? <CircularProgress size={22} /> : 'Закрыть палету'}
            </Button>
            <PalletLabelPrintButton busy={busy} canPrint={canPrint} onClick={onReprint} />
          </>
        )}
        <Button
          fullWidth
          variant="outlined"
          color="inherit"
          onClick={onCancel}
          disabled={busy}
          sx={{ minHeight: 56 }}
        >
          Отмена
        </Button>
      </DialogActions>
    </Dialog>
  );
}
